import {
  adLayoutRegex,
  bannerLayoutRegex,
  campaignLayoutRegex,
  carouselLayoutRegex,
  gridLayoutRegex,
  hesitateLayoutRegex,
  scrollTriggerLayoutRegex
} from 'utils/variables'
import { convertPageType, convertPageTypeToOld } from './convertPageType'
import { functionFromString, functionToString } from './function'

import settingContext from '@rosetta-ai/core/src/context/settings'

// after from Engine to PlatformEngine by pageType & platform
export const recommenderConverter = ({
  route,
  pageType,
  referenceItem,
  filter,
  engine,
  container,
  component,
  extraQueryParam
}) => ({
  status: 'enabled',
  layout: component.layout,
  page_type: convertPageType(pageType),
  ...(pageType === 'custom'
    ? {
        pathname:
          typeof route !== 'string' && route.toString
            ? route.toString().slice(1, -1)
            : route
      }
    : {}),
  property: {
    requestParameters: {
      ...(referenceItem
        ? {
            getCurrentItems: functionToString(referenceItem.item)
          }
        : {}),
      ...(filter
        ? {
            categorySelector: {
              // for query: filters param
              type: filter.type,
              getValue: functionToString(filter.getFilter)
            }
          }
        : {}),
      ...(extraQueryParam || {})
    },
    extra: {
      ...('replace' in component
        ? {
            hideItemWithCssSelectors: [component.replace] // 把原本的推薦隱藏
          }
        : {}),
      ...('customRenderRule' in component
        ? {
            customRenderRule: functionToString(component.customRenderRule) // 把原本的推薦隱藏
          }
        : {})
    },
    component: {
      name: container, // 自訂名稱，用於 dashboard
      ...('customCSS' in component
        ? {
            customCSS: {
              breakpoints: component.customCSS.breakpoints,
              styles: component.customCSS.deviceStyles
            }
          }
        : {}),

      // Carousel
      ...('tabs' in component
        ? {
            tabs: component.tabs
          }
        : {}),
      ...(engine &&
      (carouselLayoutRegex.test(component.layout) ||
        gridLayoutRegex.test(component.layout))
        ? {
            tabs: component.tabs
          }
        : {}),
      ...('numberOfSlide' in component
        ? {
            numberOfSlide: {
              breakpoints: [600],
              count: [
                component.numberOfSlide.desktop || 4,
                component.numberOfSlide.mobile || 2
              ]
            }
          }
        : {}),
      ...('buttons' in component ? { buttons: component.buttons } : {}),
      ...('showArrows' in component
        ? { showArrows: component.showArrows }
        : {}),

      // feature - Coupon
      ...('couponCode' in component
        ? { couponCode: component.couponCode, copiedText: component.copiedText }
        : {}),
      ...('disposable' in component
        ? { disposable: component.disposable }
        : {}),

      // feature - Hesitate
      ...(hesitateLayoutRegex.test(component.layout)
        ? {
            period: component.trigger === 'view' ? 1 : component.period || 5,
            delay: component.stayTime
          }
        : {}),

      // layout - Notification
      ...('location' in component ? { location: component.location } : {}),

      // layout - Side
      ...(['carouselSide', 'bannerSide'].includes(component.layout)
        ? { title: component.title }
        : {}),
      ...('icon' in component ? { icon: component.icon } : {}),
      ...('defaultOpen' in component
        ? { defaultOpen: component.defaultOpen }
        : {}),

      // Carousel in-page
      ...('position' in component
        ? {
            position: {
              selector: component.position.selector,
              relation: component.position.type
            }
          }
        : {}),

      // feature - Banner & Coupon

      // BannerNotification
      ...(component.layout === 'bannerNotification'
        ? {
            contentSettings: {
              breakpoints: [],
              data: component.images.map((image, i) => ({
                image,
                link: component.links && component.links[i]
              }))
            }
          }
        : {}),

      // BannerModal & BannerSide
      ...(['bannerModal', 'bannerSide'].includes(component.layout)
        ? {
            contentSettings: {
              breakpoints: component.deviceSetting.breakpoints,
              data: component.deviceSetting.deviceContents.map(
                deviceContent => ({
                  image: deviceContent.images[0],
                  link: deviceContent.links && deviceContent.links[0]
                })
              )
            }
          }
        : {}),

      // CouponNotification
      ...(component.layout === 'couponNotification'
        ? {
            contentSettings: {
              breakpoints: [],
              data: [
                {
                  image: component.image
                }
              ]
            }
          }
        : {}),

      // CouponModal
      ...(component.layout === 'couponModal'
        ? {
            contentSettings: {
              breakpoints: component.deviceSetting.breakpoints,
              data: component.deviceSetting.deviceContents.map(
                deviceContent => ({
                  image: deviceContent.images[0]
                })
              )
            }
          }
        : {})
    }
  },
  ...('startDate' in component ? { start_at: component.startDate } : {}),
  ...('endDate' in component ? { end_at: component.endDate } : {})
})

const getDefaultRenderSetting = pageType => {
  // page types: 'home' | 'category(productList)' | 'product(productDetail)' | 'checkout(cart)' | 'custom'
  return settingContext.parser.renderSetting[pageType]
}

export const recommenderConverterToOld = ({
  id,
  layout,
  page_type,
  pathname,
  start_at,
  end_at,
  updated_at,
  property: {
    requestParameters: {
      getCurrentItems,
      categorySelector,
      PLPFilter,
      ...restRequestParameters
    } = {},
    extra = {},
    component
  }
}) => {
  const convertedPageType = convertPageTypeToOld(page_type)
  const defaultRenderSetting = getDefaultRenderSetting(convertedPageType)

  return {
    ...(typeof id === 'number'
      ? {
          id
        }
      : {}),
    pageType: convertedPageType,
    PLPFilter,
    route: pathname
      ? typeof pathname === 'string'
        ? (pathname.startsWith('/') && pathname.endsWith('/')) ||
          pathname.startsWith('^') ||
          pathname.endsWith('$')
          ? new RegExp(pathname)
          : new RegExp(`^${pathname}$`)
        : pathname
      : defaultRenderSetting && defaultRenderSetting.route,
    container: `rosetta-${layout}-${id}`,
    updatedAt: updated_at,
    component: {
      layout,
      ...('logo' in component && { logo: component.logo }),
      ...('ranking' in component && { ranking: component.ranking }),
      ...('itemLimit' in component && { itemLimit: component.itemLimit }),
      ...('customCSS' in component
        ? {
            customCSS: {
              breakpoints: component.customCSS.breakpoints,
              deviceStyles: component.customCSS.styles
            }
          }
        : {}),
      ...(extra &&
      extra.hideItemWithCssSelectors &&
      extra.hideItemWithCssSelectors.length
        ? { replace: extra.hideItemWithCssSelectors[0] }
        : {}),
      ...('customRenderRule' in extra
        ? { customRenderRule: functionFromString(extra.customRenderRule) }
        : {}),

      ...('theme' in component ? { theme: component.theme } : {}),
      ...('smallTitle' in component
        ? { smallTitle: component.smallTitle }
        : {}),
      ...('largeTitle' in component
        ? { largeTitle: component.largeTitle }
        : {}),
      ...('banner' in component ? { banner: component.banner } : {}),
      ...('videoUrl' in component ? { videoUrl: component.videoUrl } : {}),
      ...('gridColumnCount' in component
        ? { gridColumnCount: component.gridColumnCount }
        : {}),
      ...('outerSettings' in component
        ? { outerSettings: component.outerSettings }
        : {}),
      ...('modalSettings' in component
        ? { modalSettings: component.modalSettings }
        : {}),
      // layout - InPage
      ...('position' in component
        ? {
            position: {
              selector: component.position.selector,
              type: component.position.relation
            }
          }
        : {}),
      ...('parentObserver' in component
        ? {
            parentObserver: component.parentObserver
          }
        : {}),

      // layout - Notification
      ...('location' in component ? { location: component.location } : {}),

      // layout - Side
      ...('title' in component ? { title: component.title } : {}),
      ...('icon' in component ? { icon: component.icon } : {}),
      ...('defaultOpen' in component
        ? { defaultOpen: component.defaultOpen }
        : {}),

      // layout - ScrollTrigger
      ...('scrollPosition' in component
        ? { scrollPosition: component.scrollPosition }
        : {}),

      // feature - Carousel
      ...(('tabs' in component &&
        (carouselLayoutRegex.test(layout) ||
          gridLayoutRegex.test(layout) ||
          adLayoutRegex.test(layout))) ||
      scrollTriggerLayoutRegex.test(layout)
        ? {
            tabs: component.tabs.map(tab => ({
              title: tab.title,
              subTitle: tab.subTitle,
              ...('engine' in tab
                ? { engine: tab.engine.id, scenarioId: tab.engine.scenarioId }
                : { customData: tab.customData })
            }))
          }
        : {}),
      ...('numberOfSlide' in component
        ? {
            // breakpoints 排序後，由大到小
            numberOfSlide: {
              ...(component.numberOfSlide.count[0]
                ? { desktop: component.numberOfSlide.count[0] }
                : {}),
              ...(component.numberOfSlide.count[1]
                ? { mobile: component.numberOfSlide.count[1] }
                : {})
            }
          }
        : {}),
      ...('buttons' in component ? { buttons: component.buttons } : {}),
      ...('showArrows' in component
        ? { showArrows: component.showArrows }
        : {}),

      // feature - Hesitate
      ...(hesitateLayoutRegex.test(layout)
        ? {
            ...('delay' in component ? { stayTime: component.delay } : {}),
            ...(component.period > 1
              ? { trigger: 'viewTimes', period: component.period }
              : { trigger: 'view' })
          }
        : {}),

      // feature - Campaign
      ...(campaignLayoutRegex.test(layout)
        ? {
            startDate: start_at,
            endDate: end_at
          }
        : {}),

      // feature - Coupon
      ...('couponCode' in component
        ? { couponCode: component.couponCode, copiedText: component.copiedText }
        : {}),
      ...('disposable' in component
        ? { disposable: component.disposable }
        : {}),

      // CouponModal
      ...('contentSettings' in component && layout === 'couponModal'
        ? {
            deviceSetting: {
              breakpoints: component.contentSettings.breakpoints,
              deviceContents: Array.isArray(component.contentSettings.data[0])
                ? component.contentSettings.data[0].map((_, i) => ({
                    images: component.contentSettings.data.map(
                      (_, j) => component.contentSettings.data[j][i].image
                    )
                  }))
                : component.contentSettings.data.map((_, i) => ({
                    images: [component.contentSettings.data[i].image]
                  }))
            }
          }
        : {}),

      // CouponNotification
      ...('contentSettings' in component && layout === 'couponNotification'
        ? {
            breakpoints: component.contentSettings.breakpoints,
            images: Array.isArray(component.contentSettings.data[0])
              ? component.contentSettings.data.map(data =>
                  data.map(d => d.image)
                )
              : [component.contentSettings.data.map(data => data.image)]
          }
        : {}),

      // feature - Banner
      ...(bannerLayoutRegex.test(layout)
        ? {
            openLinkInNewTab: component.openLinkInNewTab,
            ...('disposable' in component
              ? { disposable: component.disposable }
              : {})
          }
        : {}),

      // BannerModal & BannerSide
      ...('contentSettings' in component &&
      ['bannerModal', 'bannerSide'].includes(layout)
        ? {
            deviceSetting: {
              breakpoints: component.contentSettings.breakpoints,
              deviceContents: Array.isArray(component.contentSettings.data[0])
                ? component.contentSettings.data[0].map((_, i) => ({
                    images: component.contentSettings.data.map(
                      (_, j) => component.contentSettings.data[j][i].image
                    ),
                    links: component.contentSettings.data.map(
                      (_, j) => component.contentSettings.data[j][i].link || ''
                    )
                  }))
                : component.contentSettings.data.map((_, i) => ({
                    images: [component.contentSettings.data[i].image],
                    links: [component.contentSettings.data[i].link || '']
                  }))
            }
          }
        : {}),

      // BannerNotification
      ...('contentSettings' in component && layout === 'bannerNotification'
        ? {
            breakpoints: component.contentSettings.breakpoints,
            ...(Array.isArray(component.contentSettings.data[0])
              ? {
                  images: component.contentSettings.data.map(data =>
                    data.map(d => d.image)
                  ),
                  links: component.contentSettings.data.map(data =>
                    data.map(d => d.link)
                  )
                }
              : {
                  images: [component.contentSettings.data.map(d => d.image)],
                  links: [component.contentSettings.data.map(d => d.link)]
                })
          }
        : {}),

      // Story
      ...('highlights' in component &&
        layout.startsWith('story') && {
          highlights: component.highlights,
          styles: component.styles
        }),
      // Search
      ...(layout === 'search'
        ? {
            showTitle: component.showTitle,
            styles: component.styles,
            collections: component.collections,
            collectionTitle: component.collectionTitle,
            productTagTitle: component.productTagTitle,
            searchPlaceholder: component.searchPlaceholder
          }
        : {})
    },
    // default RenderSetting
    ...(getCurrentItems
      ? {
          referenceItem: {
            item: functionFromString(getCurrentItems)
          }
        }
      : {
          referenceItem:
            defaultRenderSetting && defaultRenderSetting.referenceItem
        }),
    ...(categorySelector
      ? {
          filter: {
            type: categorySelector.type,
            getFilter: categorySelector.getValue
              ? functionFromString(categorySelector.getValue)
              : () => categorySelector.value
          }
        }
      : {
          filter: defaultRenderSetting && defaultRenderSetting.filter
        }),
    ...(restRequestParameters ? { extraQueryParam: restRequestParameters } : {})
  }
}
